/**
 * Support Ticket Contracts
 * API contracts for support ticket management
 */

// ─────────────────────────────────────────────────────────────
// ENUMS
// ─────────────────────────────────────────────────────────────

export const TicketStatus = {
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  WAITING_CUSTOMER: 'waiting_customer',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
} as const
export type TicketStatus = (typeof TicketStatus)[keyof typeof TicketStatus]

export const TicketPriority = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
} as const
export type TicketPriority = (typeof TicketPriority)[keyof typeof TicketPriority]

export const TicketCategory = {
  BILLING: 'billing',
  TECHNICAL: 'technical',
  ACCOUNT: 'account',
  FEATURE_REQUEST: 'feature_request',
  BUG: 'bug',
  OTHER: 'other',
} as const
export type TicketCategory = (typeof TicketCategory)[keyof typeof TicketCategory]

// ─────────────────────────────────────────────────────────────
// REQUEST DTOs
// ─────────────────────────────────────────────────────────────

export interface ListTicketsRequest {
  page?: number
  limit?: number
  status?: TicketStatus
  priority?: TicketPriority
  tenantId?: string
  assignedToId?: string
  search?: string
  sortBy?: 'createdAt' | 'updatedAt' | 'priority'
  sortOrder?: 'asc' | 'desc'
}

export interface AddTicketMessageRequest {
  content: string
  isInternal?: boolean
}

export interface UpdateTicketStatusRequest {
  status: TicketStatus
}

export interface CreateTicketRequest {
  tenantId: string
  userId: string
  subject: string
  description: string
  priority: TicketPriority
  assignedToId?: string | null
  category?: TicketCategory | null
}

export interface BulkUpdateTicketsRequest {
  ids: string[]
  action: 'status' | 'priority' | 'assign' | 'category'
  /** UUID for 'assign' (or null to unassign); enum value for status/priority/category */
  value: string | null
}

export interface BulkUpdateTicketsResponse {
  updated: number
  failed: Array<{ id: string; reason: string }>
  correlationId: string
}

export interface CreatePublicTicketRequest {
  subject: string
  message: string
  category?: string
  priority?: TicketPriority
}

export interface UpdateTicketPriorityRequest {
  priority: TicketPriority
}

export interface AssignTicketRequest {
  assignedToId: string | null
}

// ─────────────────────────────────────────────────────────────
// RESPONSE DTOs
// ─────────────────────────────────────────────────────────────

export interface TicketMessageResponse {
  id: string
  ticketId: string
  authorId: string
  authorName: string
  authorEmail: string
  content: string
  isInternal: boolean
  createdAt: string
}

export interface TicketResponse {
  id: string
  tenantId: string
  tenantName: string
  userId: string
  userEmail: string
  userName: string
  assignedToId: string | null
  assignedToName: string | null
  subject: string
  status: TicketStatus
  priority: TicketPriority
  category: TicketCategory | null
  createdAt: string
  updatedAt: string
  closedAt: string | null
  lastMessageAt: string | null
  messageCount: number
}

export interface TicketDetailResponse extends TicketResponse {
  messages: TicketMessageResponse[]
}

export interface TicketListResponse {
  items: TicketResponse[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface TicketStatsResponse {
  total: number
  open: number
  inProgress: number
  waitingCustomer: number
  resolved: number
  closed: number
}

export interface AdminUserResponse {
  id: string
  email: string
  name: string
}
